import { useQuery, useMutation, useQueryClient } from 'react-query'
import { axiosClient } from 'net/ajax'
import { AxiosResponse } from 'axios'

type TokenResponse = {
    tokens: Array<{ token: string; createdAt: string }>
}

async function getToken(websiteId, integrationType) {
    const response = await axiosClient.get<TokenResponse>(
        `/ratesmgr/integrations/tokens?website_id=${websiteId}&integrations[integration_type]=${integrationType}`
    )

    const sortedTokens = response.data.tokens?.sort((a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )

    return sortedTokens?.[sortedTokens.length - 1]?.token
}

export function useGetToken({ websiteId, integrationType }) {
    const { data: token, isLoading: isGettingToken } = useQuery(
        [`tokens`, integrationType, websiteId],
        () => getToken(websiteId, integrationType)
    )

    return {
        token,
        isGettingToken,
    }
}

interface DeleteParams {
    websiteId: number
    token: string
}

async function deleteTokenAsync({ websiteId, token }: DeleteParams) {
    const response = await axiosClient.delete(
        `/ratesmgr/integrations/tokens?website_id=${websiteId}&token=${token}`
    )
    return response.data
}

export function useDeleteToken() {
    const queryClient = useQueryClient()
    const { isLoading: isDeletingToken, mutate: deleteToken } = useMutation(
        (deleteParams: DeleteParams) => deleteTokenAsync(deleteParams),
        {
            onSuccess: (_data, deleteParams) => {
                queryClient.invalidateQueries('tokens')
                queryClient.invalidateQueries(deleteParams.websiteId.toString())
            },
        }
    )

    return {
        deleteToken,
        isDeletingToken,
    }
}

type TokenCreateResponse = {
    token: string
}

async function createToken({ websiteId, integrationType }) {
    const response = await axiosClient.post<
        any,
        AxiosResponse<TokenCreateResponse>
    >('/ratesmgr/integrations/tokens', {
        website_id: websiteId,
        integrations: { integration_type: integrationType },
    })

    return response.data.token
}

export function useCreateToken() {
    const queryClient = useQueryClient()
    const {
        isLoading: isFetchingToken,
        data: token,
        mutate: create,
    } = useMutation(createToken, {
        onSuccess: (_data, websiteId) => {
            queryClient.invalidateQueries('tokens')
            queryClient.invalidateQueries(websiteId.toString())
        },
    })

    return {
        token,
        create,
        isFetchingToken,
    }
}
